import { Role } from '../types/auth';

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  leader: 'Leader',
  user: 'User',
};

// Base paths each role may access. Everyone lands on Home, a short page shaped by role (pages/HomePage).
//   admin  → everything; the Compliance overview is theirs alone (for now)
//   leader → home + dashboard + reports overview + Flow (waiting time only) + Effort (gaps only); no User Worklogs, Settings, Hygiene or My audit
//   user   → home + dashboard + My audit + User Worklogs + reports overview + Hygiene + Flow + Effort (no Settings)
// Hygiene (naming checks) is for the people who write tickets; leaders are excluded by design.
// Flow splits the same way: the checks (handoffs, rework) are for the people doing the work, while
// waiting time and stuck tickets are delivery health that every role may read. Effort splits the same
// way: the logging checks are for the people logging time, the gaps (no time on open work) are open to all.
// Planning (quarter progress and sprint reports) is delivery reporting: every role reads it.
// My audit is a person's own items from the same checks, so leaders (who are outside the checks) do not get it.
// The Guide (what each page is for, and what its words mean) is open to every signed-in user.
const ROLE_PATHS: Record<Role, string[]> = {
  admin: ['/home', '/compliance', '/my-audit', '/reports', '/reports-overview', '/hygiene', '/flow', '/effort', '/planning', '/plan', '/report', '/my-people', '/settings', '/guide'],
  leader: ['/home', '/reports-overview', '/flow', '/effort', '/planning', '/plan', '/report', '/my-people', '/guide'],
  user: ['/home', '/my-audit', '/reports', '/reports-overview', '/hygiene', '/flow', '/effort', '/planning', '/plan', '/my-people', '/guide'],
};

export function roleOf(user?: { role?: Role } | null): Role {
  return user?.role ?? 'user';
}

export function pathsForRole(role: Role): string[] {
  return ROLE_PATHS[role];
}

/** Where everyone starts: Home, one short answer per role. The fuller pages (Compliance, the Dashboard) are one press away. */
export function defaultPathForRole(_role: Role): string {
  return '/home';
}

const matches = (paths: string[], pathname: string) => paths.some((p) => pathname === p || pathname.startsWith(p + '/'));

export function canAccessPath(role: Role, pathname: string): boolean {
  return matches(ROLE_PATHS[role], pathname);
}

/** A page some role can open. A path nobody can open is simply not a page: it gets "not found", not "not for you". */
export function isKnownPath(pathname: string): boolean {
  return (Object.keys(ROLE_PATHS) as Role[]).some((role) => matches(ROLE_PATHS[role], pathname));
}
